import React from 'react';
import { Icons, ToggleGroup as UiToggleGroup, ToggleGroupItem } from '@ohif/ui-next';

/**
 * §4 In: `YES_NO` lived byte-identically in FindingsAssessment and
 * NoFindingsAssessment. Still single-sourced here, now as the default `labels` value —
 * callers may override it.
 */
const VALUES = ['yes', 'no'] as const;

const DEFAULT_LABELS = { on: 'Yes', off: 'No' };

interface ToggleGroupProps {
  value?: boolean;
  onChange: (value?: boolean) => void;
  disabled?: boolean;
  /** The group's accessible name. Required: a bare pair of radios announces nothing. */
  label: string;
  /**
   * M3.2 §5.1 removed this component's `options: ToggleOption<T>[]` generic because both
   * call sites passed booleans and `String(option.value)` collapses object keys. M4.1's
   * QualityToggle is a third call site that is still boolean-valued but reads Good/Poor,
   * so exactly the label axis comes back and nothing else.
   */
  labels?: { on: string; off: string };
}

/**
 * A checkbox-styled exclusive Yes/No pair. Clicking or keying the selected option
 * DESELECTS it, which is develop's behaviour and is what lets a reviewer undo a Yes
 * before submitting — Radix supplies it for free through `onItemDeactivate`
 * (@radix-ui/react-toggle-group/dist/index.mjs:53, wired for type="single" at :159-161).
 * The comment this file used to carry, claiming ui-next's ToggleGroup could not express
 * it, was corrected in M3.2 §6; D1 acts on the correction.
 *
 * NOT generic any more (§5.1). The old `options: ToggleOption<T>[]` API keyed its
 * children with `String(option.value)`, which collapses every object-valued option to
 * "[object Object]" — safe only because both call sites passed booleans. Radix values
 * are strings, so the boolean ↔ 'yes' | 'no' mapping lives here, at the boundary, and
 * the generality nothing used is gone.
 */
export default function ToggleGroup({
  value,
  onChange,
  disabled = false,
  label,
  labels = DEFAULT_LABELS,
}: ToggleGroupProps) {
  const selected = value === undefined ? '' : value ? 'yes' : 'no';

  return (
    <UiToggleGroup
      type="single"
      /**
       * ToggleGroupImpl builds `{ role: 'group', dir, ...toggleGroupProps }`
       * (@radix-ui/react-toggle-group/dist/index.mjs:107), so a role passed in wins. It
       * has to: the items below are role="radio" (:149), and ARIA requires those inside
       * a radiogroup, not a bare group.
       */
      role="radiogroup"
      aria-label={label}
      value={selected}
      onValueChange={next => {
        if (disabled) {
          return;
        }
        onChange(next === '' ? undefined : next === 'yes');
      }}
      className="gap-2 bg-transparent"
      /** Read by AssessmentDialog's onOpenAutoFocus to find the first unanswered row. */
      data-sycai-verdict={value === undefined ? 'unanswered' : 'answered'}
    >
      {VALUES.map(optionValue => (
        <ToggleGroupItem
          key={optionValue}
          value={optionValue}
          /**
           * `aria-disabled`, NOT Radix's `disabled`. Radix's renders a native
           * `<button disabled>` (@radix-ui/react-toggle/dist/index.mjs:19-31) and sets
           * `focusable: !disabled` on the roving-focus item (react-toggle-group:136,
           * react-roving-focus:153) — so a submitted row's recorded verdict would become
           * unreachable by keyboard, which is the defect this milestone exists to remove,
           * reintroduced on locked rows. The onValueChange guard above is what makes it
           * inert.
           */
          aria-disabled={disabled || undefined}
          /**
           * text-foreground, data-[state=on]:text-foreground and hover:!text-foreground/80
           * all override toggleVariants' own defaults (Toggle.tsx:8: the base
           * `text-primary-foreground/80`, `data-[state=on]:text-highlight`, and the default
           * variant's `hover:text-primary`) — cn()'s twMerge keeps the later class on each
           * matching modifier+group, and dropping `text-primary-foreground/80` from the
           * compiled list also takes brand.css's `button.text-primary-foreground\/80` tint
           * with it, since that rule matches on the literal class name. Resting text is
           * white at full opacity in every state now; only CheckBoxChecked's own box fill
           * (its own inline style, not currentColor) still marks the checked option orange.
           *
           * hover:'s `!` is load-bearing, not decorative: `[data-state=on]:text-foreground`
           * and `:hover:text-foreground/80` tie in specificity, and Tailwind's compiled
           * order put the data-attribute rule second — so a checked+hovered toggle stayed
           * at full opacity, undimmed, without it.
           */
          className="h-auto w-auto gap-0.5 px-1 aria-disabled:opacity-70 text-foreground data-[state=on]:text-foreground hover:!text-foreground/80"
        >
          {selected === optionValue ? (
            <Icons.CheckBoxChecked
              aria-hidden="true"
              // M5.4 D13: no colour class. The glyph paints from --legacy-primary-* in its
              // own style attribute, and after D1 .text-primary is white -- a class here
              // would do nothing today and repaint the box white the day anyone switches
              // the fill to currentColor.
              className="h-4 w-4"
            />
          ) : (
            <Icons.CheckBoxUnchecked
              aria-hidden="true"
              className="h-4 w-4"
            />
          )}
          <span className="text-xs">{optionValue === 'yes' ? labels.on : labels.off}</span>
        </ToggleGroupItem>
      ))}
    </UiToggleGroup>
  );
}
